import React, { useEffect, useState } from "react";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import { Table, Form, Spinner } from "react-bootstrap";

export default function ObjectFieldSelector({
  objectName,
  multiSelect = false,
  onSelect,
  fieldTypeFilter = "all", // e.g., 'email', 'lookup', 'email|lookup', 'all'
  excludeReadOnly = false,
  referenceObjectFilter = null, // Filter lookup fields by reference_object (e.g., 'user')
  defaultSelectedFieldName = null, // Pre-selected field name (e.g., 'ownerid')
}) {
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (objectName) {
      fetchFields(objectName);
    }
  }, [objectName, fieldTypeFilter, excludeReadOnly, referenceObjectFilter]);

  // Set default selected field when fields are loaded or defaultSelectedFieldName changes
  useEffect(() => {
    if (defaultSelectedFieldName && fields.length > 0) {
      const defaultField = fields.find(
        (f) => f.field_name === defaultSelectedFieldName
      );
      if (defaultField) {
        setSelectedFields([defaultField]);
      }
    } else if (!defaultSelectedFieldName && fields.length > 0) {
      // Clear selection if no default field name is provided
      setSelectedFields([]);
    }
  }, [fields, defaultSelectedFieldName]);

  const fetchFields = async (objectName) => {
    setLoading(true);
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
        objectName
      );
      console.log("response", response);
      if (response.success && response.data) {
        let filteredFields = response.data.fields || [];

        // Filter by data type (e.g., 'lookup', 'email', etc.)
        if (fieldTypeFilter !== "all") {
          const filters = fieldTypeFilter
            .split("|")
            .map((f) => f.trim().toLowerCase());

          filteredFields = filteredFields.filter((f) => {
            const dataType = f.data_type?.toLowerCase();
            return filters.includes(dataType);
          });
        }

        // Filter by reference object (for lookup fields)
        if (referenceObjectFilter) {
          filteredFields = filteredFields.filter((f) => {
            // Only apply to lookup fields
            if (f.data_type?.toLowerCase() === "lookup" || f.is_reference) {
              const refObject = f.reference_object?.toLowerCase();
              const targetObject = referenceObjectFilter.toLowerCase();
              return refObject === targetObject;
            }
            // For non-lookup fields, keep them if no fieldTypeFilter or if they passed the type filter
            return fieldTypeFilter === "all" || !f.data_type?.toLowerCase().includes("lookup");
          });
        }

        // Exclude read-only fields
        if (excludeReadOnly) {
          filteredFields = filteredFields.filter((f) => !f.is_readonly);
        }

        // Exclude calculated and rollupsummary fields (they cannot be updated)
        filteredFields = filteredFields.filter((f) => {
          const dataType = (f.data_type || '').toLowerCase();
          return dataType !== 'calculated' && dataType !== 'rollupsummary';
        });

        console.log("filteredFields:", filteredFields);
        setFields(filteredFields);
      } else {
        setFields([]);
      }
    } catch (err) {
      console.error("Failed to fetch object fields", err);
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelection = (field) => {
    if (multiSelect) {
      let updatedSelection = [...selectedFields];
      if (updatedSelection.includes(field)) {
        updatedSelection = updatedSelection.filter((f) => f !== field);
      } else {
        updatedSelection.push(field);
      }
      setSelectedFields(updatedSelection);
      onSelect && onSelect(updatedSelection);
    } else {
      setSelectedFields([field]);
      onSelect && onSelect(field);
    }
  };

  if (loading) {
    return <Spinner animation="border" variant="primary" />;
  }

  return (
    <div>
      <h6>Fields for {objectName}</h6>
      <Table bordered size="sm">
        <thead>
          <tr>
            <th></th>
            <th>Label</th>
            <th>Field Name</th>

            <th>Merge Field</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field.id}>
              <td>
                <Form.Check
                  type={multiSelect ? "checkbox" : "radio"}
                  name="fieldSelector"
                  checked={selectedFields.includes(field)}
                  onChange={() => handleFieldSelection(field)}
                />
              </td>
              <td>{field.field_label}</td>
              <td>{field.field_name}</td>

              <td>{`{{${field.field_name}}}`}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
